import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useHookReadout } from '../api/queries';
import { apiOn } from '../api/client';
import { hexToBig, word, wordAddr } from '../lib/abi';
import { CHAINS, explorerAddr, M_CONFIG, M_HOOK, M_POOL_ID, M_TIMELOCK, MAINNET, SEL, T_CONFIG, T_FEED, T_HOOK, T_MUSDG, T_POOL, TESTNET, type ChainId } from '../lib/chain';
import { dur, errorText, feeText, fmtUsd, fmtUtc, parseTs, short } from '../lib/format';
import { hookFromApi, readHook, type HookReading } from '../lib/hook';
import { callWords, fetchJson, rpc, RPC_TIMEOUT } from '../lib/rpc';
import { AddrLink } from './AddrLink';
import { CopyButton } from './CopyButton';
import { Stamp } from './Stamp';

const VERIFIER_INTERFACE = `interface IBellguardVerifier {
    function isOfficial(address token) external view returns (bool);
    function describe(address token) external view
        returns (
            bool official,
            string memory symbol,
            string memory name,
            bytes32 uid,
            uint256 uiMultiplier,
            bool tokenPaused,
            bool oraclePaused
        );
}`;

const HOOK_RULES = `beforeInitialize  revert unless exactly one side passes verifier.isOfficial
                  and the other side is USDG or WETH with a configured feed
beforeSwap        base = tick spacing * 0.5 bps, 1 to 1,000 bps (30 bps at tick spacing 60)
                  cap  = the larger of 300 bps and base
                  fee  = base                             while the feed updated within 3 h
                  fee  = base + 0.5 * max(0, gap - 25)    once the feed is stale, gap in bps,
                         at least 5 bps, at most cap      priced at the worse of pre- and post-swap price
                  fee  = cap                              while the feed is stale and unreadable
                  fee += 100 bps, at most cap             while the pool holds over 20 % of the supply
afterSwap         revert if the price the swap left behind calls for more than the fee charged
                  plus 10 percent (stale feed only)`;

/* ---------- the hook card ---------- */

function HookGrid({ h, at, via }: { h: HookReading; at?: Date | undefined; via?: string | undefined }) {
  const age = h.feedRead ? Date.now() - h.feedRead.updatedAt.getTime() : null;
  return (
    <>
      <dl className="live-grid">
        <dt>Base fee</dt><dd>{feeText(h.base)}{h.base !== h.baseFee ? `, the pool's tier at tick spacing ${h.tickSpacing ?? 'n/a'} (config fallback ${feeText(h.baseFee)})` : ''}</dd>
        <dt>Fee right now</dt><dd className={h.fee > h.base ? 'hot' : ''}>{feeText(h.fee)}{h.fee > h.base ? ', above base' : ', at base'}</dd>
        <dt>Feed</dt><dd>{h.stale ? <span className="cross">stale</span> : <span className="tick">fresh</span>}{age !== null && h.feedRead ? `, last set ${dur(age)} ago (${fmtUtc(h.feedRead.updatedAt)}, ${fmtUsd(h.feedRead.price, 2)} USD)` : ', last update not readable'}; stale after {h.staleAfter.toLocaleString('en-US')} s</dd>
        <dt>Gap</dt><dd>{h.gapBps} bps between pool and feed price</dd>
        <dt>Curve</dt><dd>slope {h.slope} units per bps of gap, cap {feeText(h.maxFee)}</dd>
        <dt>Float guard</dt><dd>pool holds {(h.floatBps / 100).toFixed(2)} % of supply; +{feeText(h.guardFee)} above {(h.guardBps / 100).toFixed(0)} %</dd>
        <dt>Hook</dt><dd><AddrLink addr={h.hook} chainId={TESTNET} text={short(h.hook)} /></dd>
        <dt>Pool</dt><dd>TSLA <AddrLink addr={h.stock} chainId={TESTNET} text={short(h.stock)} /> / mUSDG <AddrLink addr={h.quote} chainId={TESTNET} text={short(h.quote)} />, feed {h.feed ? <AddrLink addr={h.feed} chainId={TESTNET} text={short(h.feed)} /> : 'n/a'} (mock, set by hand)</dd>
      </dl>
      <Stamp at={at} via={via} />
    </>
  );
}

function LiveNote({ what, error }: { what: string; error: unknown }) {
  return <p className="live-note">{what} could not be read: {errorText(error)}. The page keeps trying every minute.</p>;
}

/** /api/data/hook when it answers 200 (404 means no readout yet), else the direct testnet read. */
export function HookCard() {
  const api = useHookReadout();
  const apiReading = (() => {
    if (!api.data) return null;
    try { return { h: hookFromApi(api.data), at: parseTs(api.data.ts) ?? new Date() }; } catch { return null; }
  })();
  const direct = useQuery({
    queryKey: ['hook', 'direct'],
    queryFn: readHook,
    enabled: !apiOn() || (!api.isPending && apiReading === null),
    refetchInterval: 60000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  let body: ReactNode;
  if (apiReading) body = <HookGrid h={apiReading.h} at={apiReading.at} via="worker readout" />;
  else if (direct.data) body = <HookGrid h={direct.data} />;
  else if (direct.error) body = <LiveNote what="The testnet hook" error={direct.error} />;
  else body = <p className="live-note">Waiting for the testnet RPC (needs JavaScript).</p>;
  return <div id="hook-live">{body}</div>;
}

/* ---------- the deployed table ---------- */

interface Deployed {
  chain: ChainId;
  addr: string;
  name: string;
  verify?: 'sourcify' | 'blockscout';
  kind?: 'pool';
  /** For pool rows: the hook whose poolInfo() the live column reads. */
  hook?: string;
  label: ReactNode;
  addrHref: string;
  ver: ReactNode;
  live: string;
}

const DEPLOYED: Deployed[] = [
  {
    chain: MAINNET, addr: '0x574cB84717b8aF7Df5F40a8344D783728317114f', name: 'StockVerifier', verify: 'sourcify',
    label: 'StockVerifier',
    addrHref: 'https://robinhoodchain.blockscout.com/address/0x574cB84717b8aF7Df5F40a8344D783728317114f',
    ver: <><a href="https://repo.sourcify.dev/4663/0x574cB84717b8aF7Df5F40a8344D783728317114f" rel="noopener">Sourcify</a> exact match, 8 September 2026</>,
    live: 'deployed 8 September 2026, block 57871273',
  },
  {
    chain: MAINNET, addr: M_TIMELOCK, name: 'TimelockController', verify: 'sourcify',
    label: <>TimelockController <span className="muted">(24 hour delay)</span></>,
    addrHref: explorerAddr(M_TIMELOCK, MAINNET),
    ver: <><a href={`https://repo.sourcify.dev/4663/${M_TIMELOCK}`} rel="noopener">Sourcify</a> exact match, 23 September 2026</>,
    live: 'deployed 23 September 2026, block 70812222',
  },
  {
    chain: MAINNET, addr: M_CONFIG, name: 'BellConfig', verify: 'sourcify',
    label: 'BellConfig',
    addrHref: explorerAddr(M_CONFIG, MAINNET),
    ver: <><a href={`https://repo.sourcify.dev/4663/${M_CONFIG}`} rel="noopener">Sourcify</a> exact match, 23 September 2026</>,
    live: 'deployed 23 September 2026, block 70812248',
  },
  {
    chain: MAINNET, addr: M_HOOK, name: 'BellHook', verify: 'sourcify',
    label: 'BellHook',
    addrHref: explorerAddr(M_HOOK, MAINNET),
    ver: <><a href={`https://repo.sourcify.dev/4663/${M_HOOK}`} rel="noopener">Sourcify</a> exact match, 23 September 2026</>,
    live: 'deployed 23 September 2026, block 70812325',
  },
  {
    chain: MAINNET, addr: M_POOL_ID, name: 'NVDA/USDG pool id', kind: 'pool', hook: M_HOOK,
    label: <>NVDA/USDG pool id <span className="muted">(Uniswap v4, initialised, no liquidity yet)</span></>,
    addrHref: 'https://robinhoodchain.blockscout.com/tx/0xe325776d3caacdf069d8789b30891d04ac86b39ec6b3825ca62c9efb7d630ebf',
    ver: 'initialised in the linked transaction, block 70812670',
    live: 'tick spacing 60, dynamic fee',
  },
  {
    chain: TESTNET, addr: '0xC0E4b366D22BF00816Dc88288644C06FACA312d8', name: 'testnet StockVerifier', verify: 'blockscout',
    label: 'StockVerifier',
    addrHref: 'https://explorer.testnet.chain.robinhood.com/address/0xC0E4b366D22BF00816Dc88288644C06FACA312d8',
    ver: <><a href="https://explorer.testnet.chain.robinhood.com/address/0xC0E4b366D22BF00816Dc88288644C06FACA312d8?tab=contract" rel="noopener">Blockscout</a> verified, 8 September 2026</>,
    live: 'deployed 8 September 2026, block 115699329',
  },
  {
    chain: TESTNET, addr: '0x9fE818f439Bb00c6AD7ba401CC72C9425009E35e', name: 'testnet TimelockController', verify: 'blockscout',
    label: <>TimelockController <span className="muted">(24 hour delay)</span></>,
    addrHref: 'https://explorer.testnet.chain.robinhood.com/address/0x9fE818f439Bb00c6AD7ba401CC72C9425009E35e',
    ver: <><a href="https://explorer.testnet.chain.robinhood.com/address/0x9fE818f439Bb00c6AD7ba401CC72C9425009E35e?tab=contract" rel="noopener">Blockscout</a> verified, 8 September 2026</>,
    live: 'deployed 8 September 2026, block 115699330',
  },
  {
    chain: TESTNET, addr: T_CONFIG, name: 'testnet BellConfig', verify: 'blockscout',
    label: 'BellConfig',
    addrHref: explorerAddr(T_CONFIG, TESTNET),
    ver: <><a href={`${explorerAddr(T_CONFIG, TESTNET)}?tab=contract`} rel="noopener">Blockscout</a> verified, 23 September 2026</>,
    live: 'deployed 23 September 2026, block 123336862',
  },
  {
    chain: TESTNET, addr: T_HOOK, name: 'testnet BellHook', verify: 'blockscout',
    label: 'BellHook',
    addrHref: explorerAddr(T_HOOK, TESTNET),
    ver: <><a href={`${explorerAddr(T_HOOK, TESTNET)}?tab=contract`} rel="noopener">Blockscout</a> verified, 23 September 2026</>,
    live: 'deployed 23 September 2026, block 123336899',
  },
  {
    chain: TESTNET, addr: T_FEED, name: 'MockAggregator', verify: 'blockscout',
    label: <>MockAggregator <span className="muted">(TSLA feed, owner-only, set by hand)</span></>,
    addrHref: explorerAddr(T_FEED, TESTNET),
    ver: <><a href={`${explorerAddr(T_FEED, TESTNET)}?tab=contract`} rel="noopener">Blockscout</a> verified, 23 September 2026</>,
    live: 'deployed 23 September 2026, block 123336849',
  },
  {
    chain: TESTNET, addr: T_MUSDG, name: 'MockUSDG', verify: 'blockscout',
    label: <>MockUSDG <span className="muted">(mUSDG, testnet quote token, fixed supply)</span></>,
    addrHref: explorerAddr(T_MUSDG, TESTNET),
    ver: <><a href={`${explorerAddr(T_MUSDG, TESTNET)}?tab=contract`} rel="noopener">Blockscout</a> verified, 23 September 2026</>,
    live: 'deployed 23 September 2026, block 123336843',
  },
  {
    chain: TESTNET, addr: T_POOL.id, name: 'TSLA/mUSDG pool id', kind: 'pool', hook: T_HOOK,
    label: <>TSLA/mUSDG pool id <span className="muted">(Uniswap v4, the live demo)</span></>,
    addrHref: 'https://explorer.testnet.chain.robinhood.com/tx/0xd5c29214d8e4c69dd043a32da0f5dcfe3508fce914670a7eab92f4817aca1895',
    ver: <>initialised in the linked transaction, block 123336905; demo swaps: <a href="https://explorer.testnet.chain.robinhood.com/tx/0x757a80a9fb5526090e9e7870be7a966915441afec4738726aba2e3d897728436" rel="noopener">fresh feed, fee 30 bps</a>, <a href="https://explorer.testnet.chain.robinhood.com/tx/0xd1fa780f612ded4125aac64ca68cdb0919c954449209cec72d8ca8d64e9f9e79" rel="noopener">stale feed (11,000 s old, gap 160 bps before the swap), fee 178.5 bps (priced on the 322 bps post-swap gap)</a></>,
    live: 'tick spacing 60, dynamic fee',
  },
];

async function readLive(row: Deployed): Promise<ReactNode> {
  if (row.kind === 'pool') {
    const raw = await callWords(row.hook ?? T_HOOK, SEL.poolInfo + row.addr.slice(2), 6, row.chain);
    const b = raw.slice(2);
    const stock = wordAddr(b, 0), feed = wordAddr(b, 2);
    return hexToBig('0x' + word(b, 0)) !== 0n
      ? <><span className="tick">known to the hook</span> stock <AddrLink addr={stock} chainId={row.chain} text={short(stock)} />, feed <AddrLink addr={feed} chainId={row.chain} text={short(feed)} /></>
      : <span className="cross">unknown to the hook</span>;
  }
  const code = await rpc('eth_getCode', [row.addr, 'latest'], row.chain);
  if (typeof code !== 'string') throw new Error('malformed answer');
  return code.length > 2
    ? <><span className="tick">code present</span> {((code.length - 2) / 2).toLocaleString('en-US')} bytes</>
    : <span className="cross">no code</span>;
}

interface SourcifyAnswer { match?: string }
interface BlockscoutAnswer { is_verified?: boolean; name?: string }

async function readVerification(row: Deployed): Promise<ReactNode | null> {
  if (row.verify === 'sourcify') {
    const j = (await fetchJson(`https://sourcify.dev/server/v2/contract/${row.chain}/${row.addr}`, RPC_TIMEOUT)) as SourcifyAnswer;
    if (!j.match) return null;
    return <><a href={`https://repo.sourcify.dev/${row.chain}/${row.addr}`} rel="noopener">Sourcify</a> <span className="tick">{j.match.replace('_', ' ')}</span> <span className="muted">read just now</span></>;
  }
  if (row.verify === 'blockscout') {
    const j = (await fetchJson(`${CHAINS[row.chain].explorer}/api/v2/smart-contracts/${row.addr}`, RPC_TIMEOUT)) as BlockscoutAnswer;
    if (typeof j.is_verified !== 'boolean') return null;
    return <><a href={`${explorerAddr(row.addr, row.chain)}?tab=contract`} rel="noopener">Blockscout</a> <span className={j.is_verified ? 'tick' : 'cross'}>{j.is_verified ? 'verified' : 'not verified'}</span>{j.name ? ` as ${j.name}` : ''} <span className="muted">read just now</span></>;
  }
  return null;
}

/** The static text of the row stays until the live read answers; on any failure it stays. */
function DeployedRow({ row }: { row: Deployed }) {
  const live = useQuery({ queryKey: ['deployed', 'live', row.chain, row.addr], queryFn: () => readLive(row), retry: false, staleTime: Infinity, refetchOnWindowFocus: false });
  const ver = useQuery({ queryKey: ['deployed', 'ver', row.chain, row.addr], queryFn: () => readVerification(row), enabled: !!row.verify, retry: false, staleTime: Infinity, refetchOnWindowFocus: false });
  return (
    <tr data-chain={row.chain} data-addr={row.addr} data-name={row.name} data-verify={row.verify} data-kind={row.kind}>
      <td>{row.label}</td>
      <td className="addr"><a href={row.addrHref} rel="noopener">{row.addr}</a> <CopyButton text={row.addr} label={row.name} /></td>
      <td className="ver">{ver.data ?? row.ver}</td>
      <td className="live">
        {live.data ?? (live.error
          ? <span className="pending" title={errorText(live.error)}>not read (no RPC endpoint answered)</span>
          : <span className="pending">checking</span>)}
      </td>
    </tr>
  );
}

export function DeployedTable() {
  return (
    <table className="deployed" id="deployed-table">
      <thead><tr><th>Contract</th><th>Address</th><th>Verification</th><th>On chain now</th></tr></thead>
      <tbody>
        {DEPLOYED.map((row, i) => (
          <>{(i === 0 || DEPLOYED[i - 1]?.chain !== row.chain) && (
            <tr className="chain-row" key={`chain-${row.chain}`}><th colSpan={4}>{CHAINS[row.chain].name}, chain id {row.chain}</th></tr>
          )}<DeployedRow key={`${row.chain}-${row.addr}`} row={row} /></>
        ))}
      </tbody>
    </table>
  );
}

/* ---------- the section ---------- */

export function Contracts() {
  return (
    <section className="section" id="contracts">
      <h2>Two contracts</h2>

      <div className="contract">
        <h3>The verifier: a question any contract can ask</h3>
        <p>Every official Stock Token on Robinhood Chain is deployed by the issuer's factory with CREATE2, using the token's uid as the salt. The verifier checks the proxy's code and recomputes that address from the factory, the uid the token reports and the proxy creation code. A clone on the issuer's own beacon has the right code but cannot sit at that address; a clone that reports a false uid fails the recomputation. It answers from chain state alone, with no owner, no allowlist and no maintainer. A true answer proves factory provenance; it says nothing about backing, redemption or safety.</p>
        <p>The issuer's factory exposes an official lookup, and Fletch offers an identity API. Bellguard's part is different: a recomputation that any contract can call inside a transaction, with no owner and no dependence on the factory's lookup or anyone's registry, plus pool admission in the hook. A pool, a lending market or another hook cannot open a website. isOfficial is a view function that any contract can call inside a transaction, and a token the issuer deploys tomorrow passes on the day it exists, with no update from anyone.</p>
        <pre><code>{VERIFIER_INTERFACE}</code></pre>
        <p className="status-line">Deployed on Robinhood Chain mainnet on 8 September 2026 at <a href="https://robinhoodchain.blockscout.com/address/0x574cB84717b8aF7Df5F40a8344D783728317114f" rel="noopener">0x574cB84717b8aF7Df5F40a8344D783728317114f</a>, source verified on <a href="https://sourcify.dev/#/lookup/0x574cB84717b8aF7Df5F40a8344D783728317114f" rel="noopener">Sourcify</a> (exact match). Proof established on 7 September 2026 on a mainnet fork: true for all 194 tokens in the issuer registry; false for USDG, WETH, PONS and every recorded impostor, including a beacon proxy named after Lululemon on a foreign beacon.</p>
      </div>

      <div className="contract">
        <h3>The bell hook: a fee priced by the gap</h3>
        <p>A Uniswap v4 hook for Stock Token pools that asks the verifier before it admits a pool. It refuses to create a pool unless exactly one side is a verified Stock Token and the other side is USDG or WETH with a configured Chainlink feed. While the feed is fresh the pool charges its base fee: the pool's tick spacing times 0.5 bps, which reproduces the standard 1, 5, 30 and 100 bps tiers; a pool with tick spacing 60 pays 30 basis points. When the feed has not updated for 3 hours (weekends, holidays, halts, an oracle outage) the fee grows with the measured distance between the pool price and the last feed price: a gap of up to 25 basis points is free, every basis point beyond adds half a basis point, the stale fee is never below 5 basis points, and the cap is 300 basis points or the pool's tier if that is higher. A swap is priced on the larger of the gap before it and the gap it leaves behind, and a stale feed that cannot be read charges the cap. A float guard adds 100 basis points when one pool holds more than 20 percent of a token's supply on chain. Every fee unit goes to the liquidity providers of the pool; the hook takes nothing.</p>
        <p>Priced by the gap, not by the clock. A calendar fee charges a pool sitting 0.65 percent off the frozen feed the same as one sitting 3 percent off. On a 30 basis point pool those two gaps pay 50 and 167.5 basis points. The fee follows the risk, not the day.</p>
        <pre><code>{HOOK_RULES}</code></pre>
        <p className="status-line">Tested against a fork of Robinhood Chain on 7 September 2026; the curve above was revised on 23 September 2026 against the recorded weekend of 12 to 14 September and the weekdays after it. Built for the Arbitrum Open House Singapore Online Buildathon, 14 September to 4 October 2026. Hook v2 is deployed on Robinhood Chain mainnet (4663) with an initialised NVDA/USDG pool and no liquidity yet; the TSLA/mUSDG pool on testnet 46630 is the live demo. Feeds, fallback base fee, slope, cap, staleness threshold, stale floor, deadband and float guard move through a 24 hour timelock. The initial curve was set by the deployer in the deploy run before ownership passed to the timelock; every change since waits 24 hours. The card below reads the deployed hook's own configuration, so it shows the curve the testnet deployment runs; the mainnet deployment was set to the same curve.</p>
        <div className="livebox" aria-live="polite">
          <h4>The hook right now</h4>
          <p className="live-intro">Read from the BellHook and BellConfig on testnet 46630 for the TSLA/mUSDG pool when you open this page, then every minute. The testnet feed is a mock that is set by hand, so it is stale most of the time; with a zero gap the fee stays at base, as the formula says.</p>
          <HookCard />
        </div>
      </div>

      <div className="contract" id="why-both">
        <h3>Why both</h3>
        <p>The stale-feed fee is the distance between the pool price and the Chainlink feed for the token in the pool. If that token is a copy, the feed describes a different asset and the gap means nothing. The gate guarantees the pool holds the asset the feed describes; the curve then prices the risk of that asset while the feed is frozen. A hook with only the curve prices the wrong thing. A verifier without an integration is a function nobody calls.</p>
      </div>

      <div className="contract" id="deployed">
        <h3>Deployed</h3>
        <p>Addresses as recorded on 23 September 2026. On mainnet: the verifier, and hook v2 with its configuration, its 24 hour timelock and one NVDA/USDG pool, initialised with no liquidity yet. On testnet: the same hook stack with the TSLA/mUSDG pool, the live demo. The testnet price feed is a mock, because Chainlink publishes no Stock Token feeds on the testnet. The testnet quote token is MockUSDG (mUSDG), a fixed-supply stand-in, because testnet USDG has a role-gated mint and no faucet. Every address links to its explorer page. With JavaScript on, the last column reads each address's code from the chain when you open this page, and the verification column asks Sourcify and Blockscout for their current status.</p>
        <div className="table-scroll">
          <DeployedTable />
        </div>
        <p className="status-line">The mainnet TimelockController and the testnet StockVerifier share an address, as do the mainnet BellConfig and the testnet TimelockController, because the same deployer used the same nonces on both chains; they are unrelated contracts. Source code (MIT, public before submission): <a href="https://github.com/devdotbo/bellguard" rel="noopener">github.com/devdotbo/bellguard</a>.</p>
      </div>
    </section>
  );
}
